import { Component, OnDestroy, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { Observable, of, Subject, Subscription } from 'rxjs'
import { finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { AccessTemplatesDto, ApplicationType, SyncPairInfo } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from 'src/app/account/services/account.service'
import { ApplicationCreatorState } from 'src/app/account/services/application-creator-state.service'
import { AccessScope, AccessService } from 'src/app/services/access.service'
import { RegionsService } from 'src/app/services/regions.service'

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'application-creator-connect-page',
  templateUrl: './application-creator-connect-page.component.html',
  styleUrls: ['./application-creator-connect-page.component.scss'],
  standalone: false,
})
export class ApplicationCreatorConnectPageComponent implements OnInit, OnDestroy {
  sourceAccessService: AccessService = null
  targetAccessService: AccessService = null
  private initialValidationSubscription: Subscription = null
  private destroy$ = new Subject<void>()
  sourceAccessTemplate: string = null
  targetAccessTemplate: string = null
  sourceInstallCommand: string = null
  targetInstallCommand: string = null
  Scope = AccessScope

  // StackSets data
  sourceStackSetTemplateUrl: string = null
  targetStackSetTemplateUrl: string = null
  sourceTemplateBucket: string = null
  sourceTemplateKey: string = null
  targetTemplateBucket: string = null
  targetTemplateKey: string = null
  regenerationJobId: string | null = null

  constructor(
    private applicationCreatorState: ApplicationCreatorState,
    private apiService: ApiService,
    public uiService: UiService,
    private accountService: AccountService,
    private router: Router,
    private regionsService: RegionsService
  ) {}

  get applicationType() {
    return this.applicationCreatorState.applicationType
  }

  get sourceEndpointUri() {
    return this.applicationCreatorState.sourceEndpointUri
  }

  get targetEndpointUri() {
    return this.applicationCreatorState.targetEndpointUri
  }

  get sourceAccountId() {
    return this.applicationCreatorState.sourceAccountId
  }

  get targetAccountId() {
    return this.applicationCreatorState.targetAccountId
  }

  get sourceRegion() {
    return this.applicationCreatorState.sourceRegion
  }

  get targetRegion() {
    return this.applicationCreatorState.targetRegion
  }

  get identityProviderId() {
    return this.applicationCreatorState.identityProviderId
  }

  get accountId() {
    return this.accountService.accountId
  }

  get loading() {
    return !this.sourceAccessService || subscriptionActive(this.initialValidationSubscription)
  }

  ngOnInit() {
    // Wait for account to be loaded before validating state
    // This prevents redirect on page refresh when account data is still loading
    this.accountService.accountIdStream.pipe(first(), takeUntil(this.destroy$)).subscribe((loadedAccountId) => {
      this.initializeAfterAccountLoad(loadedAccountId)
    })
  }

  private initializeAfterAccountLoad(loadedAccountId: string): void {
    // send them back to the start if we got here without account IDs and regions
    if (!this.applicationCreatorState.validAccountsAndRegions(this.regionsService)) {
      this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(loadedAccountId))
      return
    }

    let initialObservable: Observable<AccessTemplatesDto>
    const requestDto = this as SyncPairInfo
    initialObservable = this.apiService.testSyncPairAccess(requestDto).pipe(
      switchMap((accessResult) => {
        this.sourceAccessService = new AccessService(
          this.apiService,
          this.uiService,
          requestDto,
          AccessScope.SOURCE,
          accessResult
        )
        this.targetAccessService = new AccessService(
          this.apiService,
          this.uiService,
          requestDto,
          AccessScope.TARGET,
          accessResult
        )
        // Skip fetching templates only if access is properly configured for the selected method
        // For StackSets flow, we need templates even if CloudFormation access exists
        const sourceGood = this.sourceAccessService.allGood
        const targetGood = this.targetAccessService.allGood
        const stackSetsMode = this.applicationCreatorState.accessMethod === 'stacksets'
        const sourceManagedByStackSet = this.sourceAccessService.managedByStackSet
        const targetManagedByStackSet = this.targetAccessService.managedByStackSet

        const canSkipTemplates = stackSetsMode
          ? sourceGood && targetGood && sourceManagedByStackSet && targetManagedByStackSet
          : sourceGood && targetGood

        if (canSkipTemplates) {
          return of(null as AccessTemplatesDto)
        }
        return this.apiService.getAccessTemplates(
          this.accountId,
          this.sourceEndpointUri,
          this.targetEndpointUri,
          this.identityProviderId
        )
      }),
      tap((accessTemplateDto) => {
        if (!!accessTemplateDto) {
          this.sourceAccessTemplate = accessTemplateDto.sourceTemplateS3Url
          this.targetAccessTemplate = accessTemplateDto.targetTemplateS3Url
          this.sourceInstallCommand = accessTemplateDto.sourceInstallCommand
          this.targetInstallCommand = accessTemplateDto.targetInstallCommand

          // StackSets data
          this.sourceStackSetTemplateUrl = accessTemplateDto.stackSetsTemplate?.sourceStackSetTemplateS3Url
          this.targetStackSetTemplateUrl = accessTemplateDto.stackSetsTemplate?.targetStackSetTemplateS3Url
          this.sourceTemplateBucket = accessTemplateDto.stackSetsTemplate?.sourceTemplate?.bucket
          this.sourceTemplateKey = accessTemplateDto.stackSetsTemplate?.sourceTemplate?.key
          this.targetTemplateBucket = accessTemplateDto.stackSetsTemplate?.targetTemplate?.bucket
          this.targetTemplateKey = accessTemplateDto.stackSetsTemplate?.targetTemplate?.key
          this.regenerationJobId = accessTemplateDto.stackSetsTemplate?.jobId ?? null
        }
      })
    )

    this.initialValidationSubscription = initialObservable
      .pipe(finalize(() => (this.initialValidationSubscription = null)))
      .subscribe(
        () => {},
        handleArpioError(window.location.href, this.apiService, this.uiService),
        () => {
          if (this.accessConfigured) {
            this.router.navigateByUrl(this.uiService.buildNewApplicationResourcesUrl(this.accountId))
          }
        }
      )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  backButtonClick() {
    this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(this.accountId))
  }

  continueButtonClick() {
    this.router.navigateByUrl(this.uiService.buildNewApplicationResourcesUrl(this.accountId))
  }

  get loadingText(): string {
    if (this.applicationType === ApplicationType.AWS) {
      return 'Checking for AWS connectivity'
    } else if (this.applicationType === ApplicationType.AZURE) {
      return 'Checking for Azure connectivity'
    } else {
      return 'Validating access...'
    }
  }

  get stackSetsEnabled(): boolean {
    // During app creation flow, use the session-level choice from applicationCreatorState
    // This persists across page refreshes via localStorage
    if (this.applicationCreatorState.accessMethod !== null) {
      return this.applicationCreatorState.accessMethod === 'stacksets'
    }
    // Fall back to account-level setting, but only if the account has StackSets capability
    // This prevents showing StackSets instructions for accounts without the feature
    return this.accountService.stacksetsCapabilityEnabled && this.accountService.stacksetsEnabled === true
  }

  get isTestingSourceAccess(): boolean {
    return this.sourceAccessService?.subscription !== null && !this.sourceAccessService?.subscription?.closed
  }

  get isTestingTargetAccess(): boolean {
    return this.targetAccessService?.subscription !== null && !this.targetAccessService?.subscription?.closed
  }

  // Check if access is properly configured for the selected method
  // For StackSets: both must have access AND be managed by StackSets
  // For CloudFormation: both must have access (any stack is fine)
  get accessConfigured(): boolean {
    const sourceGood = this.sourceAccessService?.allGood ?? false
    const targetGood = this.targetAccessService?.allGood ?? false

    if (this.stackSetsEnabled) {
      // StackSets flow: require StackSet-managed access
      const sourceManagedByStackSet = this.sourceAccessService?.managedByStackSet ?? false
      const targetManagedByStackSet = this.targetAccessService?.managedByStackSet ?? false
      return sourceGood && targetGood && sourceManagedByStackSet && targetManagedByStackSet
    }

    // CloudFormation flow: any working access is fine
    return sourceGood && targetGood
  }

  // True when both endpoints are already managed by StackSets, so the 3-step
  // setup flow is unnecessary. The update-only component handles whatever's
  // left (e.g. template out-of-date because another app shares the same
  // source/target); if nothing is left, the page redirects on its own.
  // Mirrors application-editor's `needsStackSetsSetup` logic.
  get stackSetsSetupComplete(): boolean {
    if (!this.stackSetsEnabled) return false
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    return sourceManaged && targetManaged
  }

  onTestAccess(scope: 'source' | 'target'): void {
    if (scope === 'source') {
      this.sourceAccessService?.validateAccess()
    } else {
      this.targetAccessService?.validateAccess()
    }
  }
}
